import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Text,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { MagicLinksLoginOrCreateResponse } from "@stytch/core/public";
import { useStytch } from "@stytch/react";
import { useMutation } from "@tanstack/react-query";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import {
  useAuthContinue,
  useGetPathAfterLogin,
  useIsLoggedIn,
} from "features/auth/AuthUtils";
import { AuthPasswordForm } from "features/auth/password/AuthPasswordPage";
import {
  STYTCH_REDIRECT_URL,
  useStytchFormErrorHandler,
} from "features/auth/StytchUtils";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { LinkButton } from "shared/components/button/LinkButton";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import { DeveloperZone } from "shared/components/developer/DeveloperZone";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { AppConfig } from "shared/config/AppConfig";
import * as yup from "yup";

interface EmailFormData {
  email: string;
}

const loginSchema = yup
  .object()
  .shape({
    email: yup.string().email().required(),
  })
  .required();

const useStytchSendEmail = () => {
  const stytchClient = useStytch();
  return useMutation<MagicLinksLoginOrCreateResponse, undefined, EmailFormData>(
    {
      mutationFn: ({ email }) =>
        stytchClient.magicLinks.email.loginOrCreate(email, {
          login_magic_link_url: STYTCH_REDIRECT_URL,
          signup_magic_link_url: STYTCH_REDIRECT_URL,
        }),
    },
  );
};

export const LoginPage: React.FC = () => {
  if (!AppConfig.stytch) {
    return <Navigate to="/auth/login/simple" replace={true} />;
  }
  return <LoginPageImpl />;
};

const LoginPageImpl: React.FC = () => {
  const isLoggedIn = useIsLoggedIn();
  const onStytchError = useStytchFormErrorHandler();
  const getPathAfterLogin = useGetPathAfterLogin();
  const stytchClient = useStytch();

  const continueAuth = useAuthContinue();

  const {
    mutateAsync: sendMagicLink,
    isPending: isSendingMagicLink,
    isSuccess: didSendMagicLink,
  } = useStytchSendEmail();

  const { t } = useTranslation(["auth", "common"]);

  const form = useForm<EmailFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
    },
  });

  const [email, setEmail] = useState<string>();

  const onSubmit = useCallback((data: EmailFormData) => {
    setEmail(data.email);
  }, []);

  const onCancel = useCallback(() => {
    setEmail(undefined);
  }, []);

  if (isLoggedIn) {
    return <Navigate to={getPathAfterLogin()} replace={true} />;
  }

  if (stytchClient.session.getSync()) {
    return <Navigate to="/auth/continue" replace={true} />;
  }

  if (isSendingMagicLink) {
    return <LoadingState />;
  }

  if (email && didSendMagicLink) {
    return <LoginWithMagicLink email={email} onCancel={onCancel} />;
  }

  if (email) {
    return (
      <AuthPasswordForm
        email={email}
        onSuccess={continueAuth}
        onCancel={onCancel}
      >
        <Divider borderColor="gray.300" />
        <Button
          variant="subtle"
          p={4}
          onClick={() => sendMagicLink({ email })}
          isLoading={isSendingMagicLink}
        >
          {t("auth:loginWithMagicLink")}
        </Button>
      </AuthPasswordForm>
    );
  }

  return (
    <Box>
      <Form
        form={form}
        disableNavBlocker={true}
        onSubmit={onSubmit}
        onError={onStytchError}
      >
        <Card width="md">
          <CardHeader>
            <Text textStyle="h4">{t("auth:login")}</Text>
          </CardHeader>
          <CardBody pb="8px">
            <FormTextInput
              name="email"
              autoComplete="username"
              title={t("auth:emailAddress")}
              placeholder="user@example.com"
              autoFocus={true}
            />
          </CardBody>
          <CardFooter mt="-16px">
            <AuthHelpButton />
            <Button type="submit">{t("common:continue")}</Button>
          </CardFooter>
        </Card>
      </Form>
      <DeveloperZone>
        <LinkButton variant="subtle" linkTo="/auth/login/simple">
          {t("auth:developer.useSimpleLogin")}
        </LinkButton>
      </DeveloperZone>
    </Box>
  );
};

const LoginWithMagicLink: React.FC<{
  email: string;
  onCancel: () => void;
}> = ({ email, onCancel }) => {
  const { t } = useTranslation(["auth", "common"]);
  return (
    <Card width="md">
      <CardHeader>
        <Text textStyle="h4">{t("auth:checkYourEmail")}</Text>
        <CardCloseButton onClick={onCancel} />
      </CardHeader>
      <CardBody>
        <Trans ns="auth" i18nKey="emailSentTo" values={{ email }}>
          Email sent to
          <Text as="span" textStyle="bodyBold">
            user@example.com
          </Text>
        </Trans>
      </CardBody>
      <CardFooter>
        <AuthHelpButton />
      </CardFooter>
    </Card>
  );
};
